import React from 'react';
import { Box, Typography, Chip, useTheme, useMediaQuery } from '@mui/material';
import { 
  SmartToy as SmartToyIcon, 
  Person as PersonIcon, 
  Support as SupportIcon, 
  Business as BusinessIcon, 
  Code as CodeIcon, 
  School as SchoolIcon,
  Chat as MessageCircleIcon, // Fixing icon import
  Psychology as BrainIcon, // Fixing icon import
  TrendingUp as TrendingUpIcon,
  Headphones as HeadphonesIcon,
  ChatBubble as MessageSquareIcon, // Fixing icon import
  Edit as EditIcon,
  ElectricBolt as ZapIcon // Fixing icon import
} from '@mui/icons-material';

const EnhancedBackground = ({ bot }) => {
  const theme = useTheme();
  // Add responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Department coordinates updated for better responsive positioning
  const departments = [
    { 
      name: 'Customer Service', 
      icon: SupportIcon, 
      x: isMobile ? '40%' : '58%', 
      y: isMobile ? '25%' : '30%', 
      color: '#4caf50' 
    },
    { 
      name: 'Technical Support', 
      icon: CodeIcon, 
      x: isMobile ? '75%' : '62%', 
      y: isMobile ? '40%' : '50%', 
      color: '#2196f3' 
    },
    { 
      name: 'Accounting', 
      icon: BusinessIcon, 
      x: isMobile ? '60%' : '58%', 
      y: isMobile ? '85%' : '70%', 
      color: '#ff9800' 
    },
    { 
      name: 'Training', 
      icon: SchoolIcon, 
      x: isMobile ? '30%' : '40%',  
      y: isMobile ? '70%' : '65%', 
      color: '#9c27b0' 
    }
  ];

  // AI Services information for integrated display
  const services = [
    { 
      name: 'Chatbot Development', 
      icon: MessageCircleIcon, 
      description: 'Custom AI assistants for your business needs', 
      animationDelay: 0,
      color: '#4C85F0'
    },
    { 
      name: 'NLP Solutions', 
      icon: BrainIcon, 
      description: 'Natural language processing capabilities', 
      animationDelay: 0.2,
      color: '#9C27B0'
    },
    { 
      name: 'Data Analysis', 
      icon: TrendingUpIcon, 
      description: 'Insights extraction from your business data', 
      animationDelay: 0.4,
      color: '#FF9800'
    },
    { 
      name: '24/7 Support', 
      icon: HeadphonesIcon, 
      description: 'Always-on assistance for your customers', 
      animationDelay: 0.6,
      color: '#4CAF50'
    }
  ];

  // Interaction steps
  const interactionSteps = [
    { 
      text: 'Click the chat button in the corner', 
      icon: MessageSquareIcon, 
      animationDelay: 0.2 
    },
    { 
      text: 'Type your question or request', 
      icon: EditIcon, 
      animationDelay: 0.4 
    },
    { 
      text: 'Get instant AI-powered responses', 
      icon: ZapIcon, 
      animationDelay: 0.6 
    }
  ];

  return (
    <Box
      sx={{
        position: 'fixed', // Change to fixed to cover the entire viewport
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(18, 18, 24, 0.97)', // Darker background
        overflow: 'hidden',
        zIndex: 0, // Set low z-index so content can appear above it
      }}
    >
      {/* Circuit board pattern background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z' fill='%23ffffff' fill-opacity='0.3' fill-rule='evenodd'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Center: AI Network visualization - responsive positioning */}
      <Box
        sx={{
          position: 'absolute',
          top: '0',
          left: '0',
          width: '100%', // Cover entire width
          height: '100%', // Cover entire height
          zIndex: 1,
          opacity: 0,
          animation: 'fadeIn 1.5s forwards 0.3s'
        }}
      >
        
        {/* Central AI Bot - Positioned at the center of the page */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? 70 : 100,
            height: isMobile ? 70 : 100, // Increased size even more
            borderRadius: '50%',
            backgroundColor: bot?.customizations?.primaryColor || '#4C85F0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 0 30px ${bot?.customizations?.primaryColor || '#4C85F0'}`,
            zIndex: 3, // Increased z-index to appear above other elements
            border: '3px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <SmartToyIcon sx={{ fontSize: isMobile ? 38 : 52, color: 'white' }} />
          <Box
            sx={{
              position: 'absolute',
              width: '170%',
              height: '170%',
              borderRadius: '50%',
              border: `2px solid ${bot?.customizations?.primaryColor || '#4C85F0'}50`,
              animation: 'pulseRing 3s infinite ease-out'
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              width: '140%',
              height: '140%',
              borderRadius: '50%',
              border: `1px solid ${bot?.customizations?.primaryColor || '#4C85F0'}30`,
              animation: 'pulseRing 3s infinite ease-out 1.5s'
            }}
          />
        </Box>
        
        {/* Department nodes - Enhanced with dynamic animations and fixed positions */}
        {departments.map((dept, i) => (
          <Box
            key={`dept-${i}`}
            sx={{
              position: 'absolute',
              left: dept.x,
              top: dept.y,
              width: isMobile ? 45 : 65,
              height: isMobile ? 45 : 65,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              backgroundColor: 'rgba(30,30,35,0.8)',
              border: `2px solid ${dept.color}`,
              color: 'white',
              boxShadow: `0 0 15px ${dept.color}30`,
              zIndex: 2,
              animation: 'pulse 3s infinite ease-in-out',
              animationDelay: `${i * 0.7}s`,
              opacity: 0.9,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              transform: 'translate(-50%, -50%)', // Center the node on its coordinates
              '&:hover': {
                transform: 'translate(-50%, -50%) scale(1.05)',
                boxShadow: `0 0 25px ${dept.color}70`,
              }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isMobile ? 30 : 40,
                height: isMobile ? 30 : 40,
                color: dept.color,
                position: 'relative'
              }}
            >
              {React.createElement(dept.icon, { sx: { fontSize: isMobile ? 24 : 32 } })}
              <Box
                sx={{
                  position: 'absolute',
                  width: '120%',
                  height: '120%',
                  borderRadius: '50%',
                  border: `1px solid ${dept.color}`,
                  opacity: 0.4,
                  animation: `pulseRing 2s infinite ease-out ${i * 0.2}s`
                }}
              />
            </Box>
            <Box
              sx={{
                position: 'absolute',
                bottom: isMobile ? -22 : -28,
                fontSize: isMobile ? '0.65rem' : '0.75rem',
                fontWeight: 'bold',
                color: 'rgba(255,255,255,0.9)',
                textShadow: `0 0 8px ${dept.color}, 0 0 15px rgba(0,0,0,0.8)`,
                textAlign: 'center',
                whiteSpace: 'nowrap'
              }}
            >
              {dept.name}
            </Box>
          </Box>
        ))}

        {/* Enhanced connection lines with animated pulse effect - more responsive */}
        {departments.map((dept, i) => (
          <React.Fragment key={`connection-${i}`}>
            {/* Base connection line */}
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: '4px',
                height: Math.hypot(
                  (parseFloat(dept.x) - 50) * (isMobile ? window.innerWidth / 1.5 : window.innerWidth) / 100,
                  (parseFloat(dept.y) - 50) * (isMobile ? window.innerHeight / 1.5 : window.innerHeight) / 100
                ) / 2,
                backgroundColor: `${dept.color}40`,
                opacity: 0.6,
                transformOrigin: 'top',
                transform: `rotate(${Math.atan2(
                  parseFloat(dept.y) - 50,
                  parseFloat(dept.x) - 50
                ) * 180 / Math.PI}deg)`,
                zIndex: 1,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(to bottom, ${dept.color}90, transparent)`,
                  opacity: 0.6
                }
              }}
            />
            
            {/* Pulsing data packet along the connection - updated to work with page center */}
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                width: isMobile ? 6 : 8,
                height: isMobile ? 6 : 8,
                borderRadius: '50%',
                backgroundColor: dept.color,
                boxShadow: `0 0 10px ${dept.color}, 0 0 20px ${dept.color}80`,
                opacity: 0.8,
                zIndex: 2,
                animation: `moveAlongPath${i} 3s infinite ease-in-out ${i * 0.5}s`
              }}
            />
            
            <style jsx global>{`
              @keyframes moveAlongPath${i} {
                0% {
                  transform: translate(-50%, -50%);
                }
                50% {
                  transform: translate(
                    calc(${parseFloat(dept.x) - 50}vw),
                    calc(${parseFloat(dept.y) - 50}vh)
                  );
                }
                100% {
                  transform: translate(-50%, -50%);
                }
              }
            `}</style>
          </React.Fragment>
        ))}
      </Box>
      
      {/* Add glowing effects - responsive adjustments */}
      <Box
        sx={{
          position: 'absolute',
          top: '-100px',
          left: '-100px',
          width: isMobile ? '300px' : '400px',
          height: isMobile ? '300px' : '400px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${bot?.customizations?.primaryColor || '#4C85F0'}22, transparent 70%)`,
          opacity: 0.1,
          filter: 'blur(100px)',
          zIndex: 0
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          bottom: '-100px',
          right: '-100px',
          width: isMobile ? '300px' : '400px',
          height: isMobile ? '300px' : '400px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, #9c27b0 0%, transparent 70%)',
          opacity: 0.1,
          filter: 'blur(80px)',
          zIndex: 0
        }}
      />
    </Box>
  );
};

export default EnhancedBackground;
